import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { EventItem, Place } from '../../types/shared/event';
import { useHeader } from '../../context/HeaderContext';
import { CategoryFilter } from './CategoryFilter';
import { ListView } from './ListView';
import { MapView } from './MapView';
import { ViewToggle } from './ViewToggle';
import { DetailCard } from './DetailCard';
import { PlaceCard } from './PlaceCard';
import { DateRangeSelector } from './DateRangeSelector';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { useAirtableQuery } from '../../hooks/useAirtableQuery';
import { useTicketmasterQuery } from '../../hooks/useTicketmasterQuery';
import type { SupportedLanguages } from '../../i18n';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { logger } from '../../lib/logger';
import { getEventAdapter } from '../../lib/adapters';
import { slugify } from '../../utils/slugify';
import { SectionHeader } from '../layout/SectionHeader';
import { resolveColor } from '../../utils/colorUtils';
import { useSection } from '../../hooks/useSection';
import { stayService } from '../../lib/stay/stayService';
import { ticketmasterCache } from '../../lib/ticketmaster';
import { navigationClient } from '../../lib/navigation';

const EventApiSection: React.FC = () => {
  // Log component mount
  logger.debug('EventApiSection component mounting', {
    category: 'Navigation'
  });

  const { name } = useParams<{ name: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { setTitle } = useHeader();
  const section = useSection();
  const scrollPositionRef = useRef<number>(0);
  
  // Refs
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const lastStayDatesRef = useRef<string | null>(null);
  
  // State
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [view, setView] = useState<'map' | 'list'>('list');
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [detailBehavior, setDetailBehavior] = useState<'default' | 'pin' | 'place'>('default');
  const [selectedShowIndex, setSelectedShowIndex] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchParams, setSearchParams] = useState<{
    startDateTime?: string;
    endDateTime?: string;
  } | null>(null);
  const [isDateRangeSelectorModalOpen, setIsDateRangeSelectorModalOpen] = useState(true);
  const [datesInitialized, setDatesInitialized] = useState(false);
  const [areDatesExpired, setAreDatesExpired] = useState(false);

  // Data fetching hooks
  const { data: config, isLoading: configLoading } = useAirtableQuery.useConfig();
  const { data: translations, isLoading: translationsLoading } = useAirtableQuery.useTranslations(i18n.language as SupportedLanguages);
  
  // Load Ticketmaster config
  const { data: tmConfig, isLoading: tmConfigLoading } = useTicketmasterQuery();

  // Load events
  const { data: events = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['ticketmaster-events', tmConfig, searchParams],
    queryFn: async () => {
      if (!tmConfig || !searchParams) return [];

      const adapter = getEventAdapter('ticketmaster');
      if (!adapter) {
        throw new Error('Ticketmaster adapter not found');
      }

      return adapter.getEvents(tmConfig, searchParams);
    },
    enabled: !!tmConfig && !!searchParams && !areDatesExpired,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  // Track section view
  useEffect(() => {
    if (section?.id) {
      analyticsService.trackSectionView(
        section.id,
        'event-api',
        view,
        {
          section_name: section.names.fr || 'Unknown Section'
        }
      );
    }
  }, [section?.id, view]);

  // Check for stay date changes on component mount and focus
  useEffect(() => {
    // Function to check if stay dates have changed
    const checkStayDatesChanged = () => {
      const currentStayDates = stayService.getStayDates();
      const currentStayDatesJson = currentStayDates ? JSON.stringify(currentStayDates) : null;
      
      // If we have stay dates and they're different from what we had before
      if (currentStayDates && currentStayDatesJson !== lastStayDatesRef.current) {
        logger.debug('[EVENT_API_SECTION_STAY_DATES_CHANGED] Stay dates changed externally', {
          category: 'Stay',
          data: { 
            previousDates: lastStayDatesRef.current ? JSON.parse(lastStayDatesRef.current) : null,
            newDates: currentStayDates,
            areDatesExpired: stayService.areDatesExpired()
          }
        });
        
        // Update our reference
        lastStayDatesRef.current = currentStayDatesJson;
        
        // Check if dates are expired
        const datesExpired = stayService.areDatesExpired();
        setAreDatesExpired(datesExpired);
        
        if (!datesExpired) {
          // Clear cache for previous date range if it exists
          if (searchParams?.startDateTime && searchParams?.endDateTime) {
            ticketmasterCache.clearCacheForDateRange(
              searchParams.startDateTime,
              searchParams.endDateTime
            );
          }
          
          // Update search params with new dates
          setSearchParams({
            startDateTime: currentStayDates.startDate,
            endDateTime: currentStayDates.endDate
          });
          
          // Update local state
          setStartDate(new Date(currentStayDates.startDate));
          setEndDate(new Date(currentStayDates.endDate));
          
          // Close the date selector modal
          setIsDateRangeSelectorModalOpen(false);
          
          logger.debug('[EVENT_API_SECTION_STAY_DATES_UPDATED] Updated search params with new stay dates', {
            category: 'Stay',
            data: { 
              startDateTime: currentStayDates.startDate,
              endDateTime: currentStayDates.endDate,
              cacheCleared: true
            }
          });
        } else {
          // If dates are expired, clear search params and show date selector
          setSearchParams(null);
          setIsDateRangeSelectorModalOpen(true);
          
          logger.debug('[EVENT_API_SECTION_STAY_DATES_EXPIRED] Cleared search params due to expired dates', {
            category: 'Stay',
            data: { 
              expiredDates: currentStayDates
            }
          });
        }
      }
    };
    
    // Check on mount
    checkStayDatesChanged();
    
    // Also check when window gets focus (user might have changed dates in another tab/section)
    const handleFocus = () => {
      checkStayDatesChanged();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [searchParams]);

  // Memoized values
  const categoryColors = React.useMemo(() => {
    if (!config || !events.length) return new Map<string, string>();

    const colors = config.colors_map.split(',').map((color: string) => color.trim());
    const uniqueCategories = Array.from(new Set(events.map(event => event.category)));
    const colorMap = new Map<string, string>();

    uniqueCategories.forEach((category, index) => {
      colorMap.set(category, colors[index % colors.length]);
    });

    return colorMap;
  }, [config, events]);

  const filteredEvents = React.useMemo(() => 
    events.filter(event => {
      if (selectedType) return event.type === selectedType;
      if (selectedCategory) return event.category === selectedCategory;
      return true;
    }),
    [events, selectedCategory, selectedType]
  );

  const places = React.useMemo(() => {
    const adapter = getEventAdapter('ticketmaster');
    return adapter ? adapter.getPlaces(filteredEvents) : [];
  }, [filteredEvents]);

  // Effects
  useEffect(() => {
    if (section?.names) {
      const name = section.names[i18n.language as SupportedLanguages] || section.names.fr;
      setTitle(name);
    }
  }, [section, i18n.language, setTitle]);

  // Initialize dates from stay service
  useEffect(() => {
    if (datesInitialized) {
      return; // Skip if already initialized
    }

    // Check if dates are expired
    const datesExpired = stayService.areDatesExpired();
    setAreDatesExpired(datesExpired);

    logger.debug('[EVENT_API_SECTION_DATES_INIT] Initializing dates', {
      category: 'Stay',
      data: { 
        datesExpired,
        hasStayDates: stayService.hasStayDates(),
        savedDates: stayService.getStayDates()
      }
    });

    // Initialize with today's date and tomorrow as default
    const today = new Date();
    setStartDate(today);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(23, 59, 59);
    setEndDate(tomorrow);

    // Check if we have saved stay dates
    const savedDates = stayService.getStayDates();
    
    if (savedDates && !datesExpired) {
      // Use saved dates if available and not expired
      setStartDate(new Date(savedDates.startDate));
      setEndDate(new Date(savedDates.endDate));
      
      // Store the current stay dates for change detection
      lastStayDatesRef.current = JSON.stringify(savedDates);
      
      // Auto-search with saved dates
      setSearchParams({
        startDateTime: savedDates.startDate,
        endDateTime: savedDates.endDate
      });
      
      // Close the date selector modal
      setIsDateRangeSelectorModalOpen(false);
    } else {
      // Keep the date selector modal open
      setIsDateRangeSelectorModalOpen(true);
      
      // Clear search params if dates are expired
      if (datesExpired) {
        setSearchParams(null);
      }
    }

    setDatesInitialized(true);
  }, [datesInitialized]);

  // Parse the URL parameter to find the correct event
  useEffect(() => {
    if (name && events.length > 0) {
      // Check if the name contains date and time information
      const parts = name.split('-');
      if (parts.length >= 3) {
        // The last two parts should be date and time
        const dateStr = parts[parts.length - 2];
        const timeStr = parts[parts.length - 1];
        
        // Try to find an event with matching name, date, and time
        const event = events.find(e => {
          const eventNameSlug = slugify(e.name);
          const eventDateSlug = e.date.replace(/-/g, ''); // Convert YYYY-MM-DD to YYYYMMDD
          const eventTimeSlug = e.hour.replace(':', ''); // Convert HH:MM to HHMM
          
          // Check if the base name matches (excluding date and time parts)
          const baseNameSlug = parts.slice(0, parts.length - 2).join('-');
          
          return eventNameSlug.includes(baseNameSlug) && 
                 eventDateSlug === dateStr && 
                 eventTimeSlug === timeStr;
        });
        
        if (event) {
          setSelectedEvent(event);
          return;
        }
      }
      
      // Fallback to just matching by name if the above doesn't work
      const event = events.find(e => slugify(e.name) === name);
      if (event) {
        setSelectedEvent(event);
      }
    }
  }, [name, events]);

  // Event handlers
  const handleBackClick = () => {
    navigate('/');
  };

  const handleSearch = () => {
    if (!startDate || !endDate) return;

    // For Ticketmaster API, we need to convert dates to UTC
    const startDateTime = new Date(startDate);
    const endDateTime = new Date(endDate);

    // If start date is today, use current time
    const now = new Date();
    if (startDateTime.toDateString() === now.toDateString()) {
      startDateTime.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
    } else {
      startDateTime.setHours(0, 0, 1);
    }

    // Make sure the end date includes the full day by setting it to 23:59:59
    endDateTime.setHours(23, 59, 59);

    // Convert to UTC ISO strings for Ticketmaster API
    const startDateTimeUTC = startDateTime.toISOString();
    const endDateTimeUTC = endDateTime.toISOString();

    // Save the dates to the stay service
    stayService.setStayDates(startDate, endDate);
    
    // Update our reference for change detection
    lastStayDatesRef.current = JSON.stringify({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      lastUpdated: new Date().toISOString()
    });
    
    // Reset the expired flag since we have new dates
    setAreDatesExpired(false);

    // Clear cache for previous date range if it exists
    if (searchParams?.startDateTime && searchParams?.endDateTime) {
      ticketmasterCache.clearCacheForDateRange(
        searchParams.startDateTime,
        searchParams.endDateTime
      );
    }

    // Set search params to trigger the query
    setSearchParams({
      startDateTime: startDateTimeUTC,
      endDateTime: endDateTimeUTC
    });

    // Close the date range selector modal
    setIsDateRangeSelectorModalOpen(false);

    // Track search event
    analyticsService.trackEvent({
      event_type: 'filter',
      event_name: 'date_filter',
      event_data: {
        section_id: section.id,
        section_name: section.names.fr || 'Unknown Section',
        section_type: 'event-api',
        start_date: startDateTimeUTC,
        end_date: endDateTimeUTC
      }
    });
    
    logger.debug('[EVENT_API_SECTION_SEARCH] Search executed', {
      category: 'Stay',
      data: { 
        startDateTime: startDateTimeUTC,
        endDateTime: endDateTimeUTC,
        areDatesExpired: false, // Reset to false after search
        cacheCleared: true
      }
    });
  };

  const handleViewChange = (newView: 'map' | 'list') => {
    analyticsService.trackEvent({
      event_type: 'view',
      event_name: 'view_change',
      event_data: {
        section_id: section.id,
        section_name: section.names.fr || 'Unknown Section',
        section_type: 'event-api',
        view_type: newView
      }
    });
    setView(newView);
  };

  const handleItemClick = (item: EventItem) => {
    // Save current scroll position before navigating
    scrollPositionRef.current = window.scrollY;
    
    analyticsService.trackItemAction(
      section.id,
      item.id,
      'item_click',
      'event',
      {
        item_name: item.name,
        category: item.category,
        type: item.type,
        section_name: section.names.fr || 'Unknown Section',
        section_type: 'event-api',
        source: 'list_view'
      }
    );
    handleEventClick(item);
    
    logger.debug('Item clicked, saved scroll position', {
      category: 'Navigation',
      data: {
        scrollPosition: scrollPositionRef.current,
        itemName: item.name,
        sectionName: section.names.fr
      }
    });
  };

  const handleEventClick = (event: EventItem, behavior: 'default' | 'pin' = 'default') => {
    // Create a unique URL that includes date and time
    const eventUrl = createEventUrl(event);
    
    setSelectedEvent(event);
    setDetailBehavior(behavior);
    setSelectedPlace(null);
    setSelectedShowIndex(null);
    navigate(eventUrl, { replace: true });
  };

  const handlePlaceClick = (place: Place) => {
    if (!section?.names?.fr) return;

    if (place.shows.length === 1 && place.shows[0].events.length > 1) {
      analyticsService.trackItemAction(
        section.id,
        place.shows[0].events[0].id,
        'detail_card_view',
        'event',
        {
          item_name: place.shows[0].events[0].name,
          category: place.shows[0].events[0].category,
          type: place.shows[0].events[0].type,
          section_name: section.names.fr || 'Unknown Section',
          section_type: 'event-api'
        }
      );
      setSelectedEvent(place.shows[0].events[0]);
      setDetailBehavior('pin');
      setSelectedPlace(null);
      setSelectedShowIndex(null);
    } else {
      analyticsService.trackItemAction(
        section.id,
        place.id,
        'place_card_view',
        'place',
        {
          place_name: place.name,
          shows_count: place.shows.length,
          section_name: section.names.fr || 'Unknown Section',
          section_type: 'event-api'
        }
      );
      setSelectedPlace(place);
      setSelectedEvent(null);
      setDetailBehavior('default');
      setSelectedShowIndex(null);
    }
  };

  const handleEventFromPlace = (event: EventItem, showIndex: number) => {
    if (!section?.names?.fr) return;
    
    // Create a unique URL that includes date and time
    const eventUrl = createEventUrl(event);
    
    setSelectedEvent(event);
    setSelectedShowIndex(showIndex);
    setDetailBehavior('place');
    navigate(eventUrl, { replace: true });
  };

  // Create a unique URL for an event that includes date and time
  const createEventUrl = (event: EventItem): string => {
    if (!section?.names?.fr) return '';
    
    const sectionSlug = slugify(section.names.fr);
    const nameSlug = slugify(event.name);
    const dateSlug = event.date.replace(/-/g, ''); // Convert YYYY-MM-DD to YYYYMMDD
    const timeSlug = event.hour.replace(':', ''); // Convert HH:MM to HHMM
    
    // Utiliser la navigation relative
    return `/${sectionSlug}/${nameSlug}-${dateSlug}-${timeSlug}`;
  };

  const handleDetailClose = () => {
    // Naviguer vers la section sans paramètre d'item
    const sectionSlug = slugify(section.names.fr);
    navigate(`/${sectionSlug}`, { replace: true });
    
    setSelectedEvent(null);
    setSelectedPlace(null);
    setSelectedShowIndex(null);
    setDetailBehavior('default');
    
    // Restore scroll position after a short delay to allow for navigation
    setTimeout(() => {
      window.scrollTo(0, scrollPositionRef.current);
      
      logger.debug('Detail card closed, restored scroll position', {
        category: 'Navigation',
        data: {
          scrollPosition: scrollPositionRef.current,
          sectionName: section.names.fr
        }
      });
    }, 50);
  };

  // Loading state
  if (configLoading || translationsLoading || tmConfigLoading) {
    return <LoadingIndicator />;
  }

  // Error state
  if (!config || !translations || !section?.names || !tmConfig) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-red-600">
        <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
        <p className="text-center mb-4">Erreur lors du chargement des données</p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Retour à l'accueil
        </button>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen"
      style={{ 
        backgroundColor: resolveColor(config['sectionBack-color'], config, '#f9fafb')
      }}
    >
      <SectionHeader
        config={config}
        title={section.names[i18n.language as SupportedLanguages] || section.names.fr}
        onBackClick={handleBackClick}
        translations={translations}
      />

      {/* Filter */}
      <div className="filter-block" style={{ backgroundColor: config['major-color'] }}>
        <CategoryFilter
          selectedCategory={selectedCategory}
          selectedType={selectedType}
          onCategoryChange={setSelectedCategory}
          onTypeChange={setSelectedType}
          config={config}
          translations={translations}
          items={events}
        />
      </div>

      {/* Main content */}
      <main className="main-content">
        <div className="pt-4">
          {tmConfig && (
            <DateRangeSelector
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onSearch={handleSearch}
              config={config}
              translations={translations}
              isModalOpen={isDateRangeSelectorModalOpen}
              setIsModalOpen={setIsDateRangeSelectorModalOpen}
            />
          )}
        </div>

        {eventsLoading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingIndicator />
          </div>
        ) : events.length === 0 && searchParams === null ? (
          <div 
            className="flex items-center justify-center h-64 text-gray-500 text-center"
            dangerouslySetInnerHTML={{ __html: translations.no_result_event[i18n.language] || translations.no_result_event.fr }}
          />
        ) : events.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-500 text-center">
            <p>Aucun événement disponible pour cette période</p>
          </div>
        ) : view === 'list' ? (
          <ListView
            events={filteredEvents}
            onEventClick={handleItemClick}
            onCategorySelect={setSelectedCategory}
            onTypeSelect={setSelectedType}
            config={config}
            translations={translations}
            categoryColors={categoryColors}
          />
        ) : (
          <div 
            className="fixed inset-0 pt-[94px] pb-[40px]"
            style={{ zIndex: 1 }}
          >
            <MapView
              places={places}
              config={config}
              translations={translations}
              onEventClick={(event, behavior) => {
                handleEventClick(event, behavior);
                if (behavior === 'pin') {
                  setSelectedPlace(null);
                }
              }}
              onPlaceClick={handlePlaceClick}
              mainLocation={{
                name: config.name,
                latitude: config.latitude,
                longitude: config.longitude,
                fulladress: config.fulladress
              }}
              categoryColors={categoryColors}
              onCategorySelect={setSelectedCategory}
              onTypeSelect={setSelectedType}
              mapInstanceRef={mapInstanceRef}
            />
          </div>
        )}
      </main>

      {/* Fixed view toggle button */}
      <div 
        className="fixed left-1/2 -translate-x-1/2 z-[150] bottom-[58px]"
      >
        <ViewToggle
          view={view}
          onChange={handleViewChange}
          config={config}
          translations={translations}
        />
      </div>

      {/* Modals */}
      {selectedPlace && !selectedEvent && (
        <PlaceCard
          place={selectedPlace}
          onClose={() => setSelectedPlace(null)}
          onEventClick={handleEventFromPlace}
          onCategorySelect={setSelectedCategory}
          onTypeSelect={setSelectedType}
          config={config}
          translations={translations}
          categoryColors={categoryColors}
        />
      )}

      {selectedEvent && section?.names?.fr && (
        <DetailCard
          item={selectedEvent}
          items={filteredEvents}
          onClose={handleDetailClose}
          onItemChange={(newEvent) => {
            // Create a unique URL that includes date and time
            const eventUrl = createEventUrl(newEvent);
            
            if (detailBehavior === 'place' && selectedPlace) {
              const newIndex = selectedPlace.shows.findIndex(show => 
                show.events.some(e => e.id === newEvent.id)
              );
              if (newIndex !== -1) {
                setSelectedEvent(newEvent);
                setSelectedShowIndex(newIndex);
              }
            } else {
              setSelectedEvent(newEvent);
            }
            navigate(eventUrl, { replace: true });
          }}
          onCategorySelect={setSelectedCategory}
          onTypeSelect={setSelectedType}
          config={config}
          translations={translations}
          tableId={section.id}
          categoryColors={categoryColors}
          behavior={detailBehavior}
          showIndex={selectedShowIndex}
          place={selectedPlace}
          context={{
            translationTableId: tmConfig.translationTableId,
            locale: tmConfig.locale
          }}
          onShowPlace={(place) => {
            setSelectedEvent(null);
            setSelectedPlace(place);
          }}
        />
      )}
    </div>
  );
};

export default EventApiSection;
export { EventApiSection };